import { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../services/api';

// Token cache to avoid frequent token requests
let tokenCache = {
  token: null,
  expiresAt: null
};

export const useUser = () => {
  const { getAccessTokenSilently, user, isAuthenticated } = useAuth0();
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [isUnregistered, setIsUnregistered] = useState(false);

  // Memoized function to get token, using cache when possible
  const getToken = useCallback(async () => {
    const now = Date.now();
    
    // If we have a cached token that's not expired, use it
    if (tokenCache.token && tokenCache.expiresAt && now < tokenCache.expiresAt) {
      console.log('Using cached token');
      return tokenCache.token;
    }
    
    // Otherwise, get a new token
    console.log('Getting new token');
    try {
      const token = await getAccessTokenSilently({
        cacheMode: 'cache-only' // Try to use cache first
      });
      
      // Parse the token to get expiration time
      const tokenParts = token.split('.');
      if (tokenParts.length === 3) {
        const payload = JSON.parse(atob(tokenParts[1]));
        if (payload.exp) {
          // Set expiration 5 minutes before actual expiry to be safe
          tokenCache.expiresAt = (payload.exp * 1000) - (5 * 60 * 1000);
          tokenCache.token = token;
        }
      }
      
      return token;
    } catch (error) {
      console.error('Error getting token:', error);
      // Fall back to regular token fetch if cache fails
      const token = await getAccessTokenSilently();
      return token;
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    const fetchUser = async () => {
      if (!isAuthenticated || !user?.email) {
        setIsLoadingUser(false);
        return;
      }

      try {
        // Get the access token using our optimized method
        const token = await getToken();
        
        // Set the token in localStorage and API client
        localStorage.setItem('auth_token', token);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        // Try to get the user from the backend
        const response = await api.get('/me');
        
        // Set user data from the response
        setCurrentUser(response.data);
        
        // Specifically check the is_registered field
        if (response.data && response.data.is_registered === false) {
          setIsUnregistered(true);
        } else {
          setIsUnregistered(false);
        }
      } catch (error) {
        console.error('Error fetching user:', error);
        // If we get a 404, the user exists in Auth0 but not in our platform
        if (error.response?.status === 404) {
          setCurrentUser(null);
          setIsUnregistered(true);
        }
      } finally {
        setIsLoadingUser(false);
      }
    };

    fetchUser();
  }, [isAuthenticated, user, getToken]);

  return {
    currentUser,
    isLoadingUser,
    isUnregistered,
    setCurrentUser,
    getToken // Expose the token getter for manual use if needed
  };
}; 